import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios from '@/portainer/services/axios/axios';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { getAllSettledItems } from '@/portainer/helpers/promise-utils';
import { withError } from '@/react-tools/react-query';
import { notifyError, notifySuccess } from '@/portainer/services/notifications';
import { pluralize } from '@/portainer/helpers/strings';

import { queryKeys } from './query-keys';

export function useDeleteCustomResource(
  environmentId: EnvironmentId,
  definition: string
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (resourceNames: string[]) =>
      deleteCustomResources(resourceNames, environmentId, definition),
    onSuccess: ({ fulfilledItems, rejectedItems }) => {
      // one error notification per rejected item
      rejectedItems.forEach(({ item, reason }) => {
        notifyError(
          `Failed to remove custom resource '${item}'`,
          new Error(reason)
        );
      });

      // one success notification for all fulfilled items
      if (fulfilledItems.length) {
        notifySuccess(
          `${pluralize(
            fulfilledItems.length,
            'Custom Resource'
          )} successfully removed`,
          fulfilledItems.join(', ')
        );
      }
      queryClient.invalidateQueries(
        queryKeys.customResources(environmentId, definition)
      );
    },
    ...withError('Unable to remove custom resources'),
  });
}

function deleteCustomResources(
  resourceNames: string[],
  environmentId: EnvironmentId,
  definition: string
) {
  return getAllSettledItems(resourceNames, deleteCustomResource);

  async function deleteCustomResource(resourceName: string) {
    try {
      const params = { definition };
      await axios.delete(
        `/kubernetes/${environmentId}/customresources/${resourceName}`,
        { params }
      );
    } catch (error) {
      throw new Error(`Unable to remove custom resource '${resourceName}'`);
    }
  }
}
